import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqLoading, setReqLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const submitRequest = async (e) => {
    e.preventDefault();
    setReqLoading(true);
    try { await api.post("/auth/request-access", { name: reqName, email: reqEmail }); } catch {}
    setRequestSent(true);
    setReqLoading(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="HSH Ops" className="w-10 h-10 object-contain" />
          </div>
          <div>
            <h1 className="text-lg font-bold font-display text-slate-900">HSH Ops</h1>
            <p className="text-xs text-slate-500">Gestion opérationnelle</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold font-display text-slate-900 mb-1">Connexion</h2>
        <p className="text-sm text-slate-500 mb-6">Accédez à votre espace de gestion</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-slate-700">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid="login-email-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="login-password-input" className="mt-1" />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white">
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400">ou</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-btn"
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1a7.16 7.16 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
          </svg>
          Continuer avec Google
        </Button>
        <div className="mt-5 border-t border-slate-100 pt-4">
          {requestSent ? (
            <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-3" data-testid="request-access-success">
              Si votre demande est recevable, elle sera examinée par un administrateur.
              Vous recevrez alors un email pour créer votre mot de passe.
            </p>
          ) : !showRequest ? (
            <button type="button" onClick={() => setShowRequest(true)} data-testid="request-access-toggle"
                    className="w-full text-center text-xs text-slate-500 hover:text-slate-700 hover:underline">
              Pas de compte ? Demander l'accès
            </button>
          ) : (
            <form onSubmit={submitRequest} className="space-y-2.5" data-testid="request-access-form">
              <div className="text-xs font-semibold text-slate-700">Demander l'accès</div>
              <Input placeholder="Votre nom" value={reqName} onChange={(e) => setReqName(e.target.value)}
                     required data-testid="request-name-input" />
              <Input type="email" placeholder="Votre email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)}
                     required data-testid="request-email-input" />
              <Button type="submit" disabled={reqLoading} variant="outline" className="w-full" data-testid="request-access-submit">
                {reqLoading ? "Envoi…" : "Envoyer la demande"}
              </Button>
            </form>
          )}
        </div>
        <div className="mt-5 text-center">
          <a href="/confidentialite" data-testid="privacy-link"
             className="text-xs text-slate-400 hover:text-slate-600 hover:underline">
            Règles de confidentialité
          </a>
        </div>
      </div>
    </div>
  );
}
